import { ReactNode, useEffect, useRef } from "react";
import { Navigate, Outlet, useLocation } from "react-router";
import { useGetEligibilityCriteriaQuery } from "@lending/hooks/queries/EligibilityCriteria/useGetEligibilityCriteriaQuery";
import { useGetPersonalLoanProductsQuery } from "@lending/hooks/queries/PersonalLoanProduct/useGetPersonalLoanProductsQuery";
import { useGetInstitutionQuery } from "@lending/hooks/queries/useGetInstitutionQuery";
import { useFlowNavigation } from "@lending/navigation";
import { isMlaQuestionEnabled } from "@lending/pages/Common/ApplicationQuestions/mlaQuestion";

interface PersonalLoanGuardProps {
  children?: ReactNode;
}

function PersonalLoanGuard({ children }: PersonalLoanGuardProps) {
  const { personalLoanProductsData } = useGetPersonalLoanProductsQuery();
  const { hasEligibilityCriteria } = useGetEligibilityCriteriaQuery();
  const { institution } = useGetInstitutionQuery();
  const hasLendingQuestions = isMlaQuestionEnabled(institution.lending_ask_additional_fields);
  const { startFlow } = useFlowNavigation();
  const location = useLocation();
  const hasStartedRef = useRef(false);

  const hasPersonalLoanProducts = (personalLoanProductsData?.count ?? 0) > 0;
  const isBarePath =
    location.pathname === "/personal-loan" || location.pathname === "/personal-loan/";

  // Mirror the product-selection button on the bare product URL: startFlow
  // both clears stale flow state (so useFlowNavigation reads the right
  // productType) and navigates via useNavigateWithSearch (preserving any
  // query params). The ref guards against startFlow's unstable reference
  // re-firing the effect and re-triggering updateData on every render.
  useEffect(() => {
    if (!isBarePath) {
      hasStartedRef.current = false;
      return;
    }
    if (hasStartedRef.current || !hasPersonalLoanProducts) return;
    hasStartedRef.current = true;
    startFlow(
      "personal-loan",
      { hasEligibilityCriteria, hasLendingQuestions },
      { navigateOptions: { replace: true } }
    );
  }, [isBarePath, hasPersonalLoanProducts, hasEligibilityCriteria, hasLendingQuestions, startFlow]);

  if (!hasPersonalLoanProducts) {
    return <Navigate to="/" replace />;
  }

  if (isBarePath) {
    return null;
  }

  return children ? <>{children}</> : <Outlet />;
}

export { PersonalLoanGuard };
